/**
 * The whole translation runtime. About a hundred lines, no dependency.
 *
 * Why not a library: this app has two locales and `Intl.PluralRules` (which
 * every browser and `workerd` already ship) does the one genuinely hard part,
 * Ukrainian's four plural categories. What a library would add on top is ICU
 * message syntax and an extraction toolchain, against 22 kB (react-i18next) in
 * the chunk a QR-code landing paints from, or a babel macro in the Vite build
 * (Lingui). Neither trade bought anything here.
 *
 * What this does buy, which no library does as well:
 *
 * - **A missing key is a compile error.** The Ukrainian dictionary IS the
 *   schema (`Messages` is `typeof ukCore & typeof ukStaff`), and the English
 *   one is declared `satisfies` it. A key in one and not the other does not
 *   build; it never becomes a raw `board.title` on a screen at a tournament.
 * - **A wrong interpolation name is a compile error too.** The parameter object
 *   is inferred from the `{placeholders}` in the message literal itself, so
 *   `t("board.of", { n: 2 })` against `"{done} з {total}"` does not compile.
 *
 * Deliberately DOM-free so `bun test` can drive it directly. Everything that
 * touches `window`, `localStorage` or `<html lang>` lives in `detect.ts`.
 */

import type { Locale } from "../../shared/settings";

/**
 * A plural set, keyed by CLDR category. `other` is the only required one
 * because it is the only one every locale has.
 *
 * English uses `one`/`other`. Ukrainian uses all four: `one` (1, 21, 31…),
 * `few` (2–4, 22–24…), `many` (5–20, 25–30…, 0), and `other` (fractions:
 * "1.5 гри"). A test walks 0/1/2/5/11/21/22/101 through `Intl.PluralRules`
 * and asserts every category it can return is present in every Ukrainian entry.
 */
export interface Plural {
  readonly zero?: string;
  readonly one?: string;
  readonly two?: string;
  readonly few?: string;
  readonly many?: string;
  readonly other: string;
}

export type Message = string | Plural;

/** A flat dictionary. Keys are dotted (`"board.standings"`), never nested:
 *  nesting buys nothing here and costs the direct `keyof` that makes the types
 *  above work. */
export type Dictionary = Readonly<Record<string, Message>>;

/**
 * The shape a second locale must match: the SAME keys, and a plural where the
 * Ukrainian is a plural. Declaring `en` as `satisfies Mirror<typeof uk>` is what
 * turns a forgotten translation into a build failure. Placeholder parity is not
 * expressible here without unreadable errors, so `test/i18n.test.ts` checks
 * that separately.
 */
export type Mirror<T extends Dictionary> = {
  [K in keyof T]: T[K] extends string ? string : Plural;
};

// ---------- types that read the message literals ----------

/** `"{done} of {total}"` → `"done" | "total"`. */
type Placeholder<S extends string> = S extends `${string}{${infer P}}${infer Rest}`
  ? P | Placeholder<Rest>
  : never;

/** Every string inside a plural set, so a placeholder used in only one form
 *  still has to be supplied. */
type PluralText<M> = M extends Plural ? Extract<M[keyof M], string> : never;

type Names<M extends Message> = M extends string ? Placeholder<M> : Placeholder<PluralText<M>>;

type Values<N extends string> = { readonly [K in N]: string | number };

/**
 * No second argument at all for a message with no placeholders, which is most
 * of them, and keeps the call sites as short as the string they replaced.
 */
export type ArgsFor<M extends Message> = M extends Plural
  ? [values: Values<Names<M> | "count">]
  : [Names<M>] extends [never]
    ? []
    : [values: Values<Names<M>>];

// ---------- state ----------

/**
 * Which dictionaries a surface needs. `core` is everything reachable from the
 * main chunk (board, home, the gate, error sentences, the shared-label
 * renderers); `staff` is the five credential-gated surfaces, which are lazy
 * route chunks already and can afford one more parallel fetch.
 *
 * `push` is the odd one: it is never fetched by a page at all. The service
 * worker imports both locales of it statically and installs them itself
 * (`src/sw.ts`), because a worker woken by a push may have no network and
 * cannot wait for one. That is why this runtime is deliberately DOM-free —
 * it has to run off the page as well as on it.
 */
export type BundleName = "core" | "staff" | "push";

let locale: Locale = "uk";
let active: Record<string, Message> = {};
/** The statically imported Ukrainian core, and therefore the one dictionary
 *  that is always in memory. A key that somehow misses in the active locale
 *  falls back to it: a Ukrainian sentence on an English screen is a bad day,
 *  a raw `board.title` is a broken app. */
let fallback: Dictionary = {};

const loaded = new Map<string, Dictionary>();
const listeners = new Set<() => void>();
/** Bumped on every change so `useSyncExternalStore` has a cheap snapshot. */
let version = 0;

const pluralRules = new Map<Locale, Intl.PluralRules>();

function rulesFor(l: Locale): Intl.PluralRules {
  let r = pluralRules.get(l);
  if (r === undefined) {
    r = new Intl.PluralRules(l);
    pluralRules.set(l, r);
  }
  return r;
}

function rebuild(): void {
  const next: Record<string, Message> = {};
  for (const [key, table] of loaded) {
    if (key.startsWith(`${locale}:`)) Object.assign(next, table);
  }
  active = next;
  version += 1;
  for (const fn of listeners) fn();
}

/** Register the always-present Ukrainian core as the last-resort table. */
export function setFallback(table: Dictionary): void {
  fallback = table;
}

export function installBundle(l: Locale, name: BundleName, table: Dictionary): void {
  loaded.set(`${l}:${name}`, table);
  if (l === locale) rebuild();
}

export function hasBundle(l: Locale, name: BundleName): boolean {
  return loaded.has(`${l}:${name}`);
}

/** Swap locales. Every bundle already fetched for the new locale becomes
 *  active at once; `load.ts` is what makes sure they were fetched first. */
export function applyLocale(l: Locale): void {
  if (l === locale) return;
  locale = l;
  rebuild();
}

export function getLocale(): Locale {
  return locale;
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getVersion(): number {
  return version;
}

// ---------- rendering ----------

const PLACEHOLDER = /\{(\w+)\}/g;

function fill(template: string, values: Record<string, string | number> | undefined): string {
  if (values === undefined) return template;
  return template.replace(PLACEHOLDER, (whole, name: string) => {
    const v = values[name];
    return v === undefined ? whole : String(v);
  });
}

function pick(message: Message, values: Record<string, string | number> | undefined): string {
  if (typeof message === "string") return message;
  const count = values?.["count"];
  // A plural with no count is a bug in the call site the types should have
  // caught; render `other` rather than throwing on a live scoreboard.
  const category = typeof count === "number" ? rulesFor(locale).select(count) : "other";
  return message[category] ?? message.other;
}

/**
 * The lookup, untyped. `t()` is the typed front door; this exists for the
 * handful of places that hold a key computed at runtime (an error code, a
 * spirit category), and have already narrowed it themselves.
 */
export function translate(key: string, values?: Record<string, string | number>): string {
  const message = active[key] ?? fallback[key];
  if (message === undefined) {
    // Loud in dev, survivable in production: a tournament gets a key on screen
    // rather than a white page, and the parity test means this cannot ship.
    if (import.meta.env?.DEV === true) console.error(`[i18n] missing key: ${key}`);
    return key;
  }
  return fill(pick(message, values), values);
}
